import {
    getTestObservation,
    listTestObservations,
    listTestObservationsBySyntheticUser,
} from './queries';
import {deleteTestObservation} from './mutations';

import {GraphQLQuery} from '@aws-amplify/api';

import {
    DeleteTestObservation,
    GetTestObservation,
    ListTestObservations,
    ListTestObservationsBySyntheticUser,
    TestObservationsFilter,
} from '../types.ts';
import {getTestingApiClient} from '../../testing-api-integration/client.ts';

// APPEND-ONLY module: no add and NO UPDATE, ever — an observation is evidence and
// must not be rewritable. FNP-969 adds delete (discard the whole record), which is
// a different thing from editing one. Reuses the FNP-859 testing-api client; there
// is deliberately no second client.

// TestObservationsFilterInput accepts { syntheticUserId, actionCode, outcome,
// verificationResult, testDefinitionId }. Empty strings are dropped rather than
// sent: an empty enum value fails AppSync validation, and an empty String narrows
// to nothing.
const normalizeFilter = (filter: TestObservationsFilter): TestObservationsFilter =>
    Object.entries(filter)
          .reduce((acc: Record<string, string>, [key, value]) => {
              if (typeof value === 'string' && value !== '') {
                  acc[key] = value;
              }
              return acc;
          }, {});

export const fetchRecords = async (
    filter: TestObservationsFilter = {},
    limit?: number,
    nextToken?: string | null,
) => {
    const normalized = normalizeFilter(filter);

    const response = await getTestingApiClient().graphql<GraphQLQuery<ListTestObservations>>({
        query: listTestObservations,
        variables: {
            // Omit `filter` entirely when nothing is active — sending {} makes the
            // resolver build an empty FilterExpression.
            ...(Object.keys(normalized).length ? {filter: normalized} : {}),
            ...(typeof limit === 'number' ? {limit} : {}),
            ...(nextToken ? {nextToken} : {}),
        },
    });

    return {
        // Defensive: a GraphQL-level error leaves data undefined. The caller must
        // always receive a real array so the table never renders an undefined row.
        items: response.data?.listTestObservations?.items ?? [],
        nextToken: response.data?.listTestObservations?.nextToken ?? null,
    };
};

export const fetch = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<GetTestObservation>>({
        query: getTestObservation,
        variables: {id: id},
    });
    return response.data?.getTestObservation;
};

// Discards the whole record. There is no soft-delete and no undo: an observation
// is append-only evidence, so the only two states are "on record" and "gone".
export const deleteRecord = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<DeleteTestObservation>>({
        query: deleteTestObservation,
        variables: {id: id},
    });
    return response.data?.deleteTestObservation;
};

// Uses the synthetic-user-observed-index GSI (see queries.ts) — the cheap path for
// the "observations for this synthetic user" panel on the Synthetic Users detail page.
export const fetchRecordsBySyntheticUser = async (
    syntheticUserId: string,
    limit?: number,
    nextToken?: string | null,
) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<ListTestObservationsBySyntheticUser>>({
        query: listTestObservationsBySyntheticUser,
        variables: {
            syntheticUserId: syntheticUserId,
            ...(typeof limit === 'number' ? {limit} : {}),
            ...(nextToken ? {nextToken} : {}),
        },
    });

    return {
        items: response.data?.listTestObservationsBySyntheticUser?.items ?? [],
        nextToken: response.data?.listTestObservationsBySyntheticUser?.nextToken ?? null,
    };
};
